
import { useQuery } from "@tanstack/react-query";
import { fetchAllPuttingTrainingResults } from "@/utils/puttingResults/fetchUtils";
import { formatLevelResults, formatPuttingTrainingResults, filterByLevel } from "@/utils/puttingResults/formatUtils";
import { useSessionCheck, processResultsData } from "./usePuttingQueryUtils";
import { STALE_TIME, GC_TIME, RETRY_COUNT, getRetryDelay } from "@/utils/puttingResults/queryConfig";
import { groupChartDataByTest, getUniqueTestTypes } from "@/utils/puttingResults/chartUtils";

/**
 * Hook for fetching and managing training test results only
 * (from the training pages, with levels)
 */
export const useTrainingResults = () => {
  const { checkSession } = useSessionCheck();

  const { data: trainingResults = [], isLoading, error } = useQuery({
    queryKey: ["training-results"],
    queryFn: async () => {
      console.log('useTrainingResults queryFn called');
      const userId = await checkSession();
      console.log('Session check result - userId:', userId);
      if (!userId) {
        console.log('No userId found, returning empty array');
        return [];
      }

      try {
        // Fetch all training results from both tables
        const allResults = await fetchAllPuttingTrainingResults(userId);
        
        console.log("Fetched all training results:", allResults?.length || 0);
        
        // Format both sets of results
        const formattedLevelResults = formatLevelResults(
          allResults.filter(r => r.hasOwnProperty('level'))
        );
        const formattedPuttingTrainingResults = formatPuttingTrainingResults(
          allResults.filter(r => r.hasOwnProperty('test_level'))
        );
        
        // Combine and sort by date
        const combinedResults = [...formattedLevelResults, ...formattedPuttingTrainingResults];
        combinedResults.sort((a, b) => 
          new Date(b.test_date).getTime() - new Date(a.test_date).getTime()
        );
        
        console.log("Combined training results:", combinedResults.length);
        
        return combinedResults;
      } catch (err) {
        console.error("Error in useTrainingResults:", err);
        return [];
      }
    },
    // Add caching and retry configuration
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    // Retry logic for network errors
    retry: RETRY_COUNT,
    retryDelay: getRetryDelay,
    // Don't refetch on window focus for better performance
    refetchOnWindowFocus: false
  });

  // Filtrer les résultats par niveau - assurer une comparaison stricte de chaînes
  const level1Results = filterByLevel(trainingResults, "1");
  const level2Results = filterByLevel(trainingResults, "2");
  const level3Results = filterByLevel(trainingResults, "3");
  
  // Process data for display
  const { chartData } = processResultsData(trainingResults);
  
  // Group chart data by test type
  const chartDataByTest = groupChartDataByTest(trainingResults);
  const testTypes = getUniqueTestTypes(trainingResults);
  
  // Debug logs
  console.log("All training results:", trainingResults.length);
  console.log("Level 1 results:", level1Results.length);
  console.log("Level 2 results:", level2Results.length);
  console.log("Level 3 results:", level3Results.length);
  console.log("Test types found:", testTypes);
  
  // Check for specific test types
  const puttsMonteeResults = trainingResults.filter(r => 
    r.test_name === "Les putts en montée" || r.test_id === "putts-montee"
  );
  const puttsDescenteResults = trainingResults.filter(r => 
    r.test_name === "Les Putts en descente" || r.test_id === "putts-descente"
  );
  const puttsPenteResults = trainingResults.filter(r => 
    r.test_name === "Les Putts en Pente gauche droite" || r.test_id === "putts-pente"
  );
  const puttsPenteDroiteResults = trainingResults.filter(r => 
    r.test_name === "Les Putts en Pente droite gauche" || r.test_id === "putts-pente-droite"
  );
  
  console.log("Les putts en montée results:", puttsMonteeResults.length);
  console.log("Les Putts en descente results:", puttsDescenteResults.length);
  console.log("Les Putts en Pente gauche droite results:", puttsPenteResults.length);
  console.log("Les Putts en Pente droite gauche results:", puttsPenteDroiteResults.length);
  
  return {
    trainingResults,
    isLoading,
    error,
    chartData,
    chartDataByTest,
    testTypes,
    level1Results,
    level2Results,
    level3Results
  };
};
